// src/main.js
import { logger } from "./Logger.js";
import { CoreRepoJSONL, TAG_SYNONYMS } from "./coreRepoJSONL.js";
import { EconomySystem } from "./EconomySystem.js";
import { GuardRunner } from "./runner.js";
import { PERK_TREE, PerkSystem } from "./PerkSystem.js";
import { PassiveSystem } from "./passiveSystem.js";
import { TutorialOverlay } from "./TutorialOverlay.js";
import { audio } from "./AudioManager.js";
import { loadEconomyState, saveEconomyState, onDatabaseUpdate } from "../database/economyStore.js";
import { SwipeController } from "./SwipeController.js";
import { UIManager } from "./UIManager.js";
import { setupEvents } from "./Events.js";
import { UIRegistry } from "./UIRegistry.js";
import { AdaptiveSystem } from "./AdaptiveSystem.js";
import { VitalsTimer } from "./VitalsTimer.js";
import { RPGManager } from "./RPGManager.js";
import { FeedbackManager } from "./FeedbackManager.js";
import { StateController, GAME_STATES } from "./StateController.js";
import { AchievementManager } from "./AchievementManager.js";
import { GuardController } from "./GuardController.js";
import { GuidanceSystem } from "./GuidanceSystem.js";
import { DaySystem } from "./DaySystem.js";
import { QuestSystem } from "./QuestSystem.js";
import { StorySystem } from './StorySystem.js';
import { derivePassives } from "./Utils.js";
import { CLASSES } from "./PlayerProfile.js";

async function boot() {
  // Protocol Security Check
  if (window.location.protocol === 'file:') {
    alert('⚠️ ERROR DE CARGA: Estás abriendo el archivo localmente (file://).\n\nPor seguridad del navegador, los datos clínicos no pueden cargarse así. Debes usar un servidor local (ej. npm run dev).');
  }

  try {
    // 1. DATA BOOTSTRAP
    logger.debug("[BOOT] Loading game data...");
    const configUrl = './docs/schemas/ENGINE_GACHA_BANNERS_AND_PITY_v1.json';
    const mentorsUrl = './data/mentors.json';

    const [configRes, mentorsRes] = await Promise.all([
      fetch(configUrl).catch(err => {
        logger.error(`[BOOT] Failed to fetch ${configUrl}:`, err);
        throw new Error(`Cannot load gacha config from ${configUrl}`);
      }),
      fetch(mentorsUrl).catch(err => {
        logger.error(`[BOOT] Failed to fetch ${mentorsUrl}:`, err);
        throw new Error(`Cannot load mentors database from ${mentorsUrl}`);
      })
    ]);

    if (!configRes.ok) throw new Error(`[BOOT] Config response not OK: ${configRes.status}`);
    if (!mentorsRes.ok) throw new Error(`[BOOT] Mentors response not OK: ${mentorsRes.status}`);

    const GACHA_CONFIG = await configRes.json();
    window.MENTORS_DB = await mentorsRes.json();
    logger.debug("[BOOT] Game data loaded successfully. Mentors:", window.MENTORS_DB?.length);

    const persisted = await loadEconomyState({
      credits: 1000, badges: 10, pityCounter: 0, dailyPulls: 0,
      lastPullResetDate: new Date().toISOString().slice(0, 10),
      roster: {}, stamina: 5, last_stamina_update: Date.now(),
      firstGuard: true, studyPoints: 0, caseHistory: {},
      playerXp: 0, playerLevel: 1, burnout: 0, activeMentor: null,
      unlockedAchievements: [], dailyGuards: 0, lifetimeCleanCases: 0, 
      perfectStabilityGuards: 0, lastGuardDate: new Date().toISOString().slice(0, 10),
      discoveredPearls: {} 
    });

    const core = new CoreRepoJSONL();
    await core.loadManifest("./docs/manifest_clinical_v1.0.1.json");

    // 2. MODULAR CONTROLLER INITIALIZATION
    logger.debug("[BOOT] Initializing UI Registry...");
    const ui = UIRegistry.getElements();
    
    logger.debug("[BOOT] Creating economy system...");
    const economy = new EconomySystem(GACHA_CONFIG, persisted);
    const equipped = [persisted.activeMentor, persisted.secondaryMentor].filter(i=>i);
    const passiveSystem = new PassiveSystem(derivePassives(persisted.roster, equipped));
    const storySystem = new StorySystem();
    const runner = new GuardRunner(core, passiveSystem);
    const adaptive = new AdaptiveSystem();
    if (persisted.adaptiveState) {
        adaptive.importState(persisted.adaptiveState);
        logger.info("[BOOT] Adaptive state hydrated.");
    }
    
    // UI Manager and Simulation Controllers
    const uiManager = new UIManager(ui, GACHA_CONFIG);
    const swipeController = new SwipeController(economy, ui, uiManager);

    document.addEventListener('swipe-complete', async (e) => {
        logger.info("[Swipe Complete] Recibido evento de finalización", e.detail);
        await saveEconomyState(economy.exportState());
        refreshUI();
    });

    const vitals = new VitalsTimer(ui, audio, FeedbackManager.notify, () => guardCtrl.handleTimeout());
    
    const rpg = new RPGManager(ui, economy, PERK_TREE, {
      playClick: () => audio.playClick(),
      saveEconomyState,
      refreshUI: () => refreshUI(),
      notify: FeedbackManager.notify
    });

    let tutorialRef = null; // Set after tutorial is created, used by refreshUI

    logger.debug("[BOOT] Creating state controller...");
    const stateCtrl = new StateController(ui, {
      onEnterMenu: () => vitals.clear(),
      onEnterEncounter: () => { if (ui.learningNote) ui.learningNote.style.display = 'none'; },
      renderCaseSelection: () => renderCaseSelection()
    });

    // Cross-tab Synchronization
    onDatabaseUpdate(async () => {
        logger.info("[Sync] Actualización detectada en otra pestaña. Sincronizando estado...");
        const newState = await loadEconomyState();
        economy.importState(newState);
        refreshUI();
    });

    logger.debug("[BOOT] Creating guard controller...");
    const guardCtrl = new GuardController(
      runner, ui, stateCtrl, vitals, FeedbackManager, uiManager, audio, adaptive, passiveSystem, economy,
      {
        refreshUI: () => refreshUI(),
        terminateGuard: (success, lstate) => terminateGuard(success, lstate),
        onQuestEvent: (eventData) => processQuestEvent(eventData)
      }
    );

    // Daily-quest event processor: advances missions and pays out rewards on completion
    function processQuestEvent(eventData) {
      if (!economy.rpg?.dailyQuests?.length) return;
      const completed = economy.rpg.checkQuests(eventData);
      if (!completed.length) return;
      completed.forEach(q => {
        const r = q.reward || {};
        if (r.credits) economy.addCredits(r.credits);
        if (r.xp) economy.addPlayerXp(r.xp);
        FeedbackManager.notify(`🎯 Misión cumplida: ${q.title} (+${r.credits || 0} Cr, +${r.xp || 0} XP)`, 'success');
        audio.playSuccess();
      });
      economy.adaptiveState = adaptive.exportState();
      saveEconomyState(economy.exportState());
      refreshUI();
    }

    // 3. UI SYNC & REWARD LOGIC
    function refreshUI() {
      economy.adaptiveState = adaptive.exportState(); // Sync adaptive state to economy container
      const s = economy.exportState();
      uiManager.update(s, economy.getLevelState());
      uiManager.updateLearning(guardCtrl.learningState);
      
      if (ui.spCount) ui.spCount.textContent = s.skillPoints;
      if (ui.burnoutUI) {
        const b = s.burnout || 0;
        ui.burnoutUI.textContent = `${Math.round(b)}%`;
        ui.burnoutUI.style.color = b > 70 ? 'var(--accent)' : 'inherit';
      }
      
      rpg.renderQuests();
      renderAchievementTray();
      uiManager.applyVisualBurnout(s.burnout || 0);
      
      if (ui.licensesCount) ui.licensesCount.textContent = s.licenses || 0;
      if (ui.badgesCount) ui.badgesCount.textContent = s.badges || 0;

      // Pity Meter Update
      const pityCounterText = document.getElementById('pity-counter-text');
      const pityBarFill = document.getElementById('pity-bar-fill');
      if (pityCounterText && pityBarFill) {
          const pity = s.pityCounter || 0;
          pityCounterText.textContent = `${pity} / 50`;
          pityBarFill.style.width = `${Math.min((pity / 50) * 100, 100)}%`;
      }

      // Todo abierto desde el día 1: nada se oculta por nivel.
      if (ui.btnAtlas) ui.btnAtlas.style.display = 'block';
      if (ui.btnRanking) ui.btnRanking.style.display = 'block';
      if (ui.btnPerks) ui.btnPerks.style.display = 'block';
      
      // Day Briefing Update
      const briefing = DaySystem.getBriefing(economy);
      const btnEndDay = document.getElementById('btn-end-day');
      const bTitle = document.getElementById('briefing-title');
      const bTurn = document.getElementById('briefing-turn');
      const bLine = document.getElementById('briefing-drv-line');

      if (bTitle) bTitle.textContent = briefing.title;
      if (bTurn) bTurn.textContent = briefing.turn;
      if (bLine) bLine.textContent = `"${briefing.drVLine}"`;
      if (btnEndDay) {
          btnEndDay.style.display = (s.guardsToday || 0) > 0 ? 'flex' : 'none';
      }
      
      GuidanceSystem.update(economy, ui);
      if (tutorialRef) tutorialRef.checkProgress(economy.state);
    }
    window.refreshUI = refreshUI; // Global exposure for tutorial or debug

    // Stamina Countdown Loop (Fase 4)
    setInterval(() => {
      if (economy.stamina >= economy.maxStamina) {
        if (ui.staminaTimer) ui.staminaTimer.textContent = '';
      } else {
        const now = Date.now();
        const msSinceUpdate = now - economy.last_stamina_update;
        const recoveryRateMs = 15 * 60 * 1000;
        
        if (msSinceUpdate >= recoveryRateMs) {
          economy.recalculateStamina();
          refreshUI();
        } else {
          const remainingMs = recoveryRateMs - (msSinceUpdate % recoveryRateMs);
          const mins = Math.floor(remainingMs / 60000);
          const secs = Math.floor((remainingMs % 60000) / 1000);
          if (ui.staminaTimer) {
            ui.staminaTimer.textContent = ` (${mins}:${String(secs).padStart(2, '0')})`;
          }
        }
      }
    }, 1000);


    function renderAchievementTray() {
      if (!ui.achievementTray) return;
      ui.achievementTray.innerHTML = '';
      const unlocked = economy.state.unlockedAchievements || [];
      AchievementManager.ACHIEVEMENTS.forEach(ach => {
          const isUnlocked = unlocked.includes(ach.id);
          const icon = document.createElement('div');
          icon.className = 'achievement-icon' + (isUnlocked ? ' unlocked' : '');
          icon.textContent = ach.name.split(' ')[0];
          icon.title = `${ach.name}: ${ach.desc}`;
          ui.achievementTray.appendChild(icon);
      });
    }

    async function terminateGuard(success, learningState) {
      if (!learningState) {
        learningState = { xp: 0, total: 0, correct: 0, insights: [] };
      }
      
      let finalReport = null;
      if (runner.currentSession) {
        finalReport = runner.finalize();
      }

      stateCtrl.changeState(GAME_STATES.REPORT);
      vitals.clear();
      if (success) {
        const stats = {
            xp: learningState.xp,
            correct: learningState.correct,
            total: learningState.total,
            avgStability: learningState.totalStability / (learningState.total || 1),
            finalReport: finalReport,
            isRounds: !!runner.currentSession?.isRounds,
            lethals: runner.currentSession?.results?.filter(r => r.isLethal).length || 0
        };

        const rewards = economy.processRewards({
            ...stats,
            unpaid: !!runner.currentSession?.isUnpaid,
            passiveSystem,
            perkModifiers: runner.modifiers
        });
        
        economy.state.dailyGuards++;
        if (learningState.incorrect === 0) {
          economy.state.lifetimeCleanCases += (runner.currentSession?.encounters?.length || 0);
          
          // Perk: stamina_refund_chance (Fase 2)
          const refundChance = runner.modifiers?.guard?.staminaRefundChance || 0;
          if (refundChance > 0 && Math.random() < refundChance) {
            economy.stamina = Math.min(economy.maxStamina, economy.stamina + 1);
            FeedbackManager.notify("☕ ¡Bono Café de Urgencias: +1 de Energía recuperada!", "success");
          }

          // Burnout decay: -10% on perfect guard (Fase 3)
          economy.addBurnout(-10);
        }
        if (runner.currentSession && runner.currentSession.patientState.estabilidad >= 100) {
          economy.state.perfectStabilityGuards++;
        }

        // Award Affinity (Fase 2)
        if (finalReport && finalReport.mentor_affinity_gain > 0) {
          const equippedMentors = [economy.state.activeMentor, economy.state.secondaryMentor].filter(Boolean);
          equippedMentors.forEach(mentorId => {
            const levelUpInfo = economy.addAffinity(mentorId, finalReport.mentor_affinity_gain);
            if (levelUpInfo && levelUpInfo.leveledUp) {
              FeedbackManager.notify(`📈 ¡Afinidad con ${economy.roster[mentorId].name || mentorId} aumentada a Nivel ${levelUpInfo.newLevel}!`, 'success');
            }
          });
        }
        
        const newAchs = AchievementManager.checkNewAchievements(runner.currentSession, economy);
        newAchs.forEach(ach => {
          if (economy.awardAchievement(ach)) {
            uiManager.achievement(ach.name, ach.desc);
            audio.playSuccess();
          }
        });

        // [Quests] Guard-level missions (completed without abandoning / without lethal errors)
        const hadLethal = !!runner.currentSession?.results?.some(r => r.isLethal);
        const preQuestCount = economy.rpg?.dailyQuests?.filter(q => q.completed).length || 0;
        processQuestEvent({ 
            guardComplete: true, 
            hadLethal,
            roundsComplete: stats.isRounds
        });
        const postQuestCount = economy.rpg?.dailyQuests?.filter(q => q.completed).length || 0;
        const newQuestsCompleted = postQuestCount - preQuestCount;

        stats.credits = rewards.credits;
        stats.unpaid = !!runner.currentSession?.isUnpaid;
        DaySystem.logGuard(economy, stats, finalReport, newQuestsCompleted);

        // Story Beats Check
        const sessionData = {
          lethals: runner.currentSession?.results?.filter(r => r.isLethal).length || 0,
          isCorrection: false, // We would need adaptive state for this, omit for now
          chainSurvived: finalReport && finalReport.totalEncounters > 1 && stats.correct === finalReport.totalEncounters && stats.lethals === 0
        };
        const beats = storySystem.checkBeats(economy.state, adaptive, sessionData);
        beats.forEach(beat => {
          if (beat.type === 'minor') {
            FeedbackManager.notify(beat.text, 'info', { speaker: beat.speaker });
          } else {
            // Major beats will be pushed to a queue to be shown in the report
            stats.majorBeat = beat;
          }
        });

        // Show the beautiful report overlay (Fase 4)
        await uiManager.showShiftReport(rewards, stats);
      } else {
        // [Quests] Still check for lethal failure
        const hadLethal = !!runner.currentSession?.results?.some(r => r.isLethal);
        processQuestEvent({ guardComplete: true, hadLethal });
        
        DaySystem.logGuard(economy, { correct: learningState.correct, total: learningState.total, avgStability: learningState.totalStability / (learningState.total || 1), xp: learningState.xp, credits: 0, unpaid: !!runner.currentSession?.isUnpaid }, finalReport, 0);
      }
      
      const caseId = runner.currentSession?.caseData?.id;

      if (!success && hadLethal) {
          const action = await uiManager.showFailureReport();
          if (action === 'retry' && caseId) {
              // Retry without charging stamina
              actions.startSpecificCase(caseId, true);
              return; // Do not go to menu
          }
      }

      stateCtrl.changeState(GAME_STATES.MENU);
      economy.adaptiveState = adaptive.exportState();
      saveEconomyState(economy.exportState());
      refreshUI();
    }

    function renderCaseSelection() {
      const cases = [
        { id: 'CASE_SWIPE_EASY_001', title: 'Triage y Cuidados Iniciales', diff: 'EASY', tags: ['General'] },
        { id: 'CASE_SWIPE_STANDARD_001', title: 'Crisis de Asma y Oxígeno', diff: 'STANDARD', tags: ['Urgencias'] },
        { id: 'CASE_SWIPE_STANDARD_002', title: 'Crisis Hiperglucémica Joven', diff: 'STANDARD', tags: ['Endocrinología'] },
        { id: 'CASE_SWIPE_STANDARD_003', title: 'Preeclampsia Trimestre 3', diff: 'STANDARD', tags: ['Ginecobstetricia'] },
        { id: 'CASE_SWIPE_HARD_001', title: 'IAM con Elevación de ST', diff: 'HARD', tags: ['Cardiología'] },
        { id: 'CASE_SWIPE_HARD_002', title: 'Choque Séptico Pulmonar', diff: 'HARD', tags: ['Infectología'] },
        { id: 'CASE_SWIPE_HARD_003', title: 'Trauma Cerrado y Hemorragia', diff: 'HARD', tags: ['Trauma'] }
      ];
      ui.caseList.innerHTML = '';
      cases.forEach(c => {
        const card = document.createElement('div');
        card.className = 'case-card card';
        card.innerHTML = `<strong>${c.title}</strong><div class="case-meta">${c.diff}</div>`;
        card.onclick = () => {
          swipeController.startSimulation(c.id, () => stateCtrl.changeState(GAME_STATES.MENU));
          stateCtrl.changeState(GAME_STATES.SIMULATION);
        };
        ui.caseList.appendChild(card);
      });
    }

    // 4. ACTION DEFINITIONS
    const actions = {
      startGuard: () => {
        stateCtrl.changeState(GAME_STATES.SPECIALTY_SELECT);
        // Todo el hospital está abierto: ninguna sala se bloquea por nivel o licencias.
        const modifiers = PerkSystem.getModifiers(economy.rpg);
        const showTags = modifiers?.ui?.showCaseTagsPreAccept;

        document.querySelectorAll('.specialty-btn[data-tag]').forEach(btn => {
            const tag = btn.getAttribute('data-tag');
            btn.classList.remove('locked');
            btn.title = '';

            // Display case tags under the label if showTags is true
            let tagsEl = btn.querySelector('.specialty-tags');
            if (showTags) {
                if (!tagsEl) {
                    tagsEl = document.createElement('div');
                    tagsEl.className = 'specialty-tags';
                    tagsEl.style.cssText = 'font-size: 0.65rem; opacity: 0.8; margin-top: 4px; color: var(--accent); text-transform: capitalize;';
                    btn.appendChild(tagsEl);
                }
                const synonyms = TAG_SYNONYMS[tag] || [];
                const displayTags = synonyms.filter(s => s.toLowerCase() !== tag.toLowerCase());
                tagsEl.textContent = displayTags.slice(0, 3).join(', ');
            } else if (tagsEl) {
                tagsEl.remove();
            }
        });
      },
      startSpecialtyGuard: async (tag) => {
        // Todo abierto: ninguna sala exige nivel ni licencias. La energía tampoco
        // bloquea el acceso — sin energía la guardia es "de estudio" (XP reducido,
        // sin créditos), pero siempre se puede jugar.
        guardCtrl.resetLearningState();
        FeedbackManager.notify(`Preparando Guardia de ${tag}...`, 'info');
        try {
          await core.loadGroup(tag);
          const filter = tag === 'General' ? {} : { tags: [tag] };
          await runner.prepareShift(filter, adaptive.buildUserProfile(economy, 'Normal'));
        } catch (err) {
          logger.warn(`[Guard] No se pudo preparar la guardia de ${tag}:`, err.message);
          runner.currentSession = null;
        }

        if (!(runner.currentSession && runner.currentSession.encounters.length > 0)) {
          FeedbackManager.notify(`No hay casos suficientes para ${tag} todavía. Prueba otra sala o la Guardia Rápida.`, 'warning');
          stateCtrl.changeState(GAME_STATES.MENU);
          return;
        }

        // La energía se cobra solo cuando ya hay casos confirmados.
        const paid = economy.consumeStamina();
        if (!paid) {
          runner.currentSession.isUnpaid = true;
          FeedbackManager.notify('☕ Sin energía: guardia de estudio (XP reducido, sin créditos). ¡El estudio nunca se bloquea!', 'info');
        } else {
          // Narrative message if paid
          const specialty = runner.currentSession.specialty || tag;
          setTimeout(() => {
            FeedbackManager.notify(`Has sido asignado a ${specialty}. Demuestra que mereces estar aquí.`, 'info', {
              duration: 4000,
              speaker: 'Dra. V',
              icon: 'stethoscope'
            });
          }, 500);
        }

        stateCtrl.changeState(GAME_STATES.ENCOUNTER);
        guardCtrl.showNextEncounter();
      },
      startSpecificCase: async (caseId, isRetry = false) => {
        guardCtrl.resetLearningState();
        try {
          await core.loadGroup('General'); // Make sure cases are loaded
          await runner.prepareShift({ ids: [caseId] }, adaptive.buildUserProfile(economy, 'Normal'));
        } catch (err) {
          logger.warn(`[Guard] No se pudo preparar el caso ${caseId}:`, err.message);
          runner.currentSession = null;
        }

        if (!(runner.currentSession && runner.currentSession.encounters.length > 0)) {
          FeedbackManager.notify(`No se encontró el caso ${caseId}.`, 'warning');
          stateCtrl.changeState(GAME_STATES.MENU);
          return;
        }

        if (!isRetry) {
            const paid = economy.consumeStamina();
            if (!paid) {
              runner.currentSession.isUnpaid = true;
              FeedbackManager.notify('☕ Sin energía: guardia de estudio (XP reducido, sin créditos).', 'info');
            }
        } else {
            runner.currentSession.isRetry = true;
        }

        stateCtrl.changeState(GAME_STATES.ENCOUNTER);
        guardCtrl.showNextEncounter();
      },
      startLibrary: async () => {
        // Modo estudio dinámico: sin reloj, sin costo y con acceso a TODO el banco
        // (no solo dificultad baja) — el mazo prioriza los temas débiles del perfil.
        guardCtrl.resetLearningState();
        stateCtrl.changeState(GAME_STATES.ENCOUNTER, { isLibrary: true });
        await runner.prepareShift({}, adaptive.buildUserProfile(economy, 'Normal', true));
        if (runner.currentSession) runner.currentSession.isLibrary = true;
        guardCtrl.showNextEncounter();
      },
      startRounds: async () => {
        guardCtrl.resetLearningState();
        
        const failed = adaptive.state.failedCases || [];
        const forgotten = Object.keys(economy.getForgottenPearls() || {});
        let ids = [...new Set([...failed, ...forgotten])];
        
        if (ids.length === 0) {
            FeedbackManager.notify("Sin pendientes — la Dra. V está satisfecha.", "success");
            return;
        }
        
        // Limitar a 5 casos
        ids = ids.slice(0, 5);

        FeedbackManager.notify("Comenzando Pase de Visita. Modo sin costo, doble XP por acierto.", "info");
        stateCtrl.changeState(GAME_STATES.ENCOUNTER, { isLibrary: true });
        await runner.prepareShift({ ids }, adaptive.buildUserProfile(economy, 'Normal', true));
        
        if (runner.currentSession) {
            runner.currentSession.isLibrary = true; // Sin reloj
            runner.currentSession.isRounds = true;
            runner.currentSession.isUnpaid = true;
        }
        
        guardCtrl.showNextEncounter();
      },
      startSimulation: () => stateCtrl.changeState(GAME_STATES.CASE_SELECT),
      endDay: async () => {
        const summary = DaySystem.endDay(economy);
        if (summary) {
            const beats = storySystem.checkBeats(economy.state, adaptive, null);
            beats.forEach(beat => {
              if (beat.type === 'minor') {
                FeedbackManager.notify(beat.text, 'info', { speaker: beat.speaker });
              } else {
                summary.majorBeat = beat;
              }
            });
            uiManager.renderEndOfDay(summary);
            stateCtrl.changeState('END_OF_DAY');
            saveEconomyState(economy.exportState());
        }
      },
      startNewDay: () => {
        economy.rpg.dailyQuests = QuestSystem.generateDailyQuests(economy.state.dayNumber || 1, economy.exportState());
        saveEconomyState(economy.exportState());
        stateCtrl.changeState(GAME_STATES.MENU);
        refreshUI();
      },
      goBack: () => stateCtrl.changeState(GAME_STATES.MENU),
      openHub: (initialTab = 'tab-roster') => {
        uiManager.switchTab(initialTab);
        const renderR = () => {
          uiManager.renderRoster(
            economy.state.roster, 
            economy.state.activeMentor, 
            economy.state.secondaryMentor,
            (id, slot) => {
               if (economy.equipMentor(id, slot)) {
                 audio.playSuccess();
                 FeedbackManager.notify(`Mentor equipado en slot ${slot}.`, 'success');
                  const equipped = [economy.state.activeMentor, economy.state.secondaryMentor].filter(i=>i);
                  passiveSystem.passives = derivePassives(economy.state.roster, equipped);
                  economy.adaptiveState = adaptive.exportState();
                  saveEconomyState(economy.exportState());
                  renderR();
                }
            }
          );
        };
        renderR();
        rpg.renderPerkTree();
        uiManager.renderAtlas(economy.state.discoveredPearls || {}, 'all', PerkSystem.getModifiers(economy.rpg));
        uiManager.renderRanking(economy.exportState());
        ui.hubModal.style.display = 'flex';
      },
      closeHub: () => ui.hubModal.style.display = 'none',
      openPerks: () => {
        rpg.renderPerkTree();
        const m = document.getElementById('perk-tree-modal');
        if (m) m.style.display = 'flex';
      },
      closePerks: () => {
        const m = document.getElementById('perk-tree-modal');
        if (m) m.style.display = 'none';
      },
      closeCaseSelection: () => stateCtrl.changeState(GAME_STATES.MENU),
      pull1: async () => { 
        uiManager.setLoading('pull1', true);
        try {
            const r = await economy.pull(1); 
            if (r) {
                if (r.success === false) {
                    FeedbackManager.notify(r.reason, 'warning');
                } else {
                    uiManager.showPullAnimation(r); 
                    refreshUI(); 
                    saveEconomyState(economy.exportState()); 
                }
            } 
        } finally {
            uiManager.setLoading('pull1', false);
        }
      },
      pull10: async () => { 
        uiManager.setLoading('pull10', true);
        try {
            const rs = await economy.pull(10); 
            if (rs) {
                if (rs.success === false) {
                    FeedbackManager.notify(rs.reason, 'warning');
                } else {
                    uiManager.showPullAnimation(rs); 
                    economy.adaptiveState = adaptive.exportState();
                    refreshUI(); 
                    saveEconomyState(economy.exportState()); 
                }
            } 
        } finally {
            uiManager.setLoading('pull10', false);
        }
      },
      buyStamina: () => {
        const res = economy.buyStamina();
        if (res.success) {
          audio.playSuccess();
          FeedbackManager.notify(`☕ Estamina comprada con éxito. Energía: ${res.newStamina}/${economy.maxStamina}`, "success");
          refreshUI();
          saveEconomyState(economy.exportState());
        } else {
          audio.playFailure();
          FeedbackManager.notify(res.reason, "error");
        }
      },
      exchangeMentor: (mentorId) => {
        const res = economy.exchangeMentor(mentorId);
        if (res.success) {
          audio.playSuccess();
          FeedbackManager.notify(`🔬 ${res.mentor.name} contratado/a con éxito en tu Roster!`, "success");
          
          const equipped = [economy.state.activeMentor, economy.state.secondaryMentor].filter(i=>i);
          passiveSystem.passives = derivePassives(economy.state.roster, equipped);

          refreshUI();
          saveEconomyState(economy.exportState());
          
          // Re-render roster list immediately if hub is open
          if (ui.hubModal.style.display === 'flex') {
            actions.openHub();
          }
        } else {
          audio.playFailure();
          FeedbackManager.notify(res.reason, "error");
        }
      },
      openSettings: () => {
        const modal = document.getElementById('settings-modal');
        if (modal) {
          // Initialize controls to audio state
          const sfxToggle = document.getElementById('settings-sfx-toggle');
          const musicToggle = document.getElementById('settings-music-toggle');
          const volumeSlider = document.getElementById('settings-volume-slider');
          const volumeValue = document.getElementById('settings-volume-value');
          
          if (sfxToggle) sfxToggle.checked = audio.sfxEnabled;
          if (musicToggle) musicToggle.checked = audio.musicEnabled;
          if (volumeSlider) volumeSlider.value = Math.round(audio.volume * 100);
          if (volumeValue) volumeValue.textContent = `${Math.round(audio.volume * 100)}%`;
          
          modal.style.display = 'flex';
        }
      },
      closeSettings: () => {
        const modal = document.getElementById('settings-modal');
        if (modal) modal.style.display = 'none';
      },
      toggleSFX: (enabled) => {
        audio.setSFXEnabled(enabled);
      },
      toggleMusic: (enabled) => {
        audio.setMusicEnabled(enabled);
      },
      changeVolume: (val) => {
        audio.setVolume(val);
      },
      resetProgress: () => {
        if (confirm("⚠️ ¿Estás seguro/a de que deseas restablecer TODO tu progreso? Se borrarán tus mentores, nivel y licencias de forma permanente. El juego se recargará.")) {
          localStorage.clear();
          const req = indexedDB.deleteDatabase('GachaHospitalDB');
          req.onsuccess = () => {
            location.reload();
          };
          req.onerror = () => {
            console.error("Error deleting database");
            location.reload();
          };
          req.onblocked = () => {
            console.warn("Database deletion blocked, reloading anyway");
            location.reload();
          };
        }
      },
      closeMap: () => {
        if (ui.specialtyModal) ui.specialtyModal.style.display = 'none';
        refreshUI();
      },
      openPerks: () => {
        const modal = document.getElementById('perk-tree-modal');
        if (modal) {
            rpg.renderPerkTree();
            modal.style.display = 'flex';
        }
      },
      closePerks: () => {
        const modal = document.getElementById('perk-tree-modal');
        if (modal) modal.style.display = 'none';
      },
      playClick: () => audio.playClick()
    };

    logger.debug("[BOOT] Setting up events...");
    setupEvents(ui, actions);

    // --- EVENT DELEGATION FOR DYNAMIC MAP ---
    document.addEventListener('click', (e) => {
      // Specialty Selection
      const specBtn = e.target.closest('.specialty-btn');
      if (specBtn && ui.specialtyGrid?.contains(specBtn)) {
        const tag = specBtn.getAttribute('data-tag');
        actions.startSpecialtyGuard(tag);
        actions.closeMap();
      }
      
      // Close Map Modal
      if (e.target.closest('#close-specialty-selection')) {
        actions.closeMap();
      }
    });

    // Ensure initial stamina isn't 0 for returning/stuck users
    if (economy.stamina === 0) {
      logger.info("[BOOT] Low stamina detected, granting 1 point for recovery.");
      economy.stamina = 1; 
    }

    refreshUI(); 



    // Class selection: renders the 3 archetypes and applies the chosen one.
    // Mechanics (time bias in VitalsTimer, surgeon damage in runner) already read classId.
    function showClassSelection() {
      const modal = document.getElementById('class-selection-modal');
      const grid = modal?.querySelector('.class-grid');
      if (!modal || !grid) return;
      grid.innerHTML = '';
      Object.values(CLASSES).forEach(cls => {
        const card = document.createElement('div');
        card.className = 'card';
        card.style.cssText = 'padding:16px; cursor:pointer; border:2px solid var(--ink); display:flex; flex-direction:column; gap:6px; text-align:left;';
        card.innerHTML = `
          <strong style="font-family:var(--font-heading); font-size:1rem;">${cls.name}</strong>
          <p style="font-size:0.8rem; opacity:0.85; margin:0;">${cls.desc}</p>
          <div style="font-size:0.75rem; color:var(--primary);">✔ ${cls.passive.desc}</div>
          <div style="font-size:0.75rem; color:var(--accent);">⚡ ${cls.active.name}: ${cls.active.desc}</div>
          <div style="font-size:0.7rem; opacity:0.7;">⚠ ${cls.sesgo.desc}</div>
        `;

        card.onclick = () => {
          if (economy.rpg.setClass(cls.id)) {
            audio.playSuccess();
            economy.adaptiveState = adaptive.exportState();
            saveEconomyState(economy.exportState());
            modal.style.display = 'none';
            FeedbackManager.notify(`Clase elegida: ${cls.name}`, 'success');
            refreshUI();
            
            // Balatro Approach: Force first case if not done
            if (!localStorage.getItem('gachaHospital.firstCaseForced')) {
                localStorage.setItem('gachaHospital.firstCaseForced', 'true');
                actions.startSpecificCase('CORE_STEMI_001'); // Ensure this triggers the actual case start
            }
          }
        };
        grid.appendChild(card);
      });
      modal.style.display = 'flex';
    }
    const maybeShowClassSelection = () => { if (!economy.rpg.isClassSelected()) showClassSelection(); };

    // Narrative Prologue (Balatro Approach)
    const prologueOverlay = document.getElementById('narrative-prologue-overlay');
    const prologueText = document.getElementById('prologue-text');
    const prologueNextBtn = document.getElementById('prologue-next-btn');
    
    const prologueTexts = [
      "Primer día de residencia. Te tocó la guardia de esta noche.",
      "Regla de la casa: cada decisión enseña. Los errores aquí no matan — pero se estudian.",
      "Cama 3. Paciente inestable. Demuéstrame cómo piensas. — Dra. V"
    ];
    let prologueIndex = 0;

    if (prologueOverlay && prologueText && prologueNextBtn && !localStorage.getItem('gachaHospital.prologueCompleted')) {
        prologueOverlay.style.display = 'flex';
        prologueText.textContent = prologueTexts[0];
        
        prologueNextBtn.addEventListener('click', () => {
            audio.playClick();
            prologueIndex++;
            if (prologueIndex < prologueTexts.length) {
                prologueText.textContent = prologueTexts[prologueIndex];
                if (prologueIndex === prologueTexts.length - 1) {
                    prologueNextBtn.textContent = "COMENZAR GUARDIA";
                }
            } else {
                prologueOverlay.style.opacity = '0';
                prologueOverlay.style.transition = 'opacity 0.5s ease';
                setTimeout(() => { 
                    prologueOverlay.style.display = 'none'; 
                    localStorage.setItem('gachaHospital.prologueCompleted', 'true');
                    maybeShowClassSelection(); 
                }, 500);
            }
        });
    } else {
        maybeShowClassSelection();
    }

    // Onboarding — checkProgress is called from refreshUI on every state update
    const tutorial = new TutorialOverlay(uiManager, economy, actions);
    tutorialRef = tutorial;
    tutorial.startOnboarding();

    // Keyboard shortcuts
    window.addEventListener('keydown', (e) => {
      const s = stateCtrl.currentState;
      if (s === GAME_STATES.FEEDBACK && e.key === 'Enter') ui.nextBtn?.click();
      if (s === GAME_STATES.ENCOUNTER && e.key >= '1' && e.key <= '4') {
          ui.options?.querySelectorAll('button')[parseInt(e.key) - 1]?.click();
      }
      if (s === GAME_STATES.NODE_RESULT && e.key === 'Enter') ui.closeNodeResult?.click();
    });

    window.addEventListener('mentorUnlockedByMastery', (e) => {
      const mentor = e.detail;
      if (mentor) {
        FeedbackManager.notify(`¡Desbloqueaste a ${mentor.name} por Dominio de ${mentor.specialty}!`, 'success');
      }
    });

    stateCtrl.syncVisualTheme();

    // [F5.3] Portal Honesto - Direct mode launch based on URL
    const urlParams = new URLSearchParams(window.location.search);
    const mode = urlParams.get('mode');
    if (mode === 'rounds' && actions.startRounds) {
        setTimeout(() => actions.startRounds(), 100);
    } else if (mode === 'study' && actions.startSimulation) {
        setTimeout(() => actions.startSimulation(), 100);
    }

    logger.info("[BOOT] APPLICATION READY - Gacha Hospital initialized successfully!");

  } catch (err) {
    logger.error("[BOOT] CRITICAL ERROR:", err);
    logger.error("[BOOT] Error stack:", err.stack);
    const container = document.body;
    container.innerHTML = `
      <div style="padding: 40px; text-align: center; font-family: monospace; background: #fff3cd; color: #856404; border: 2px solid #ffc107; margin: 20px;">
        <h2>⚠️ Error de Carga</h2>
        <p>No se pudo cargar correctamente el juego.</p>
        <p><strong>${err.message}</strong></p>
        <p style="font-size: 0.85em; opacity: 0.8;">Intenta recargar la página (F5) o contacta al soporte.</p>
        <pre style="text-align: left; background: white; padding: 15px; border-radius: 4px; overflow-x: auto; max-height: 200px;">${err.stack}</pre>
      </div>
    `;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  boot();
});
